import { useCallback, useMemo } from 'react';
import { useAudioPlayer } from '@contexts/AudioPlayerContext';
import { useAutoScroll } from '@hooks/useAutoScroll';
import {
  filterTranscriptSegments,
  findActiveSegment,
  findActiveWord,
  groupTranscriptSegments
} from '@models/transcripts/transcriptModel';

export function useTranscriptViewController({ transcript, searchQuery }) {
  const { currentTime, seekTo, play, isPlaying } = useAudioPlayer();
  const activeSegment = useMemo(
    () => findActiveSegment(transcript.segments, currentTime),
    [transcript.segments, currentTime]
  );
  const activeWord = useMemo(
    () => findActiveWord(activeSegment, currentTime),
    [activeSegment, currentTime]
  );
  const autoScroll = useAutoScroll(activeSegment?.start);
  const filteredSegments = useMemo(
    () => filterTranscriptSegments(transcript.segments, searchQuery),
    [transcript.segments, searchQuery]
  );
  const transcriptParagraphs = useMemo(
    () => groupTranscriptSegments(filteredSegments),
    [filteredSegments]
  );

  const handleTimestampClick = useCallback((time) => {
    seekTo(time);
    if (!isPlaying) {
      play();
    }
  }, [isPlaying, play, seekTo]);

  return {
    filteredSegments,
    transcriptParagraphs,
    activeSegment,
    activeWord,
    autoScrollEnabled: autoScroll.enabled,
    setAutoScrollEnabled: autoScroll.setEnabled,
    autoScrollPaused: autoScroll.isPaused,
    resumeAutoScroll: autoScroll.resume,
    activeElementRef: autoScroll.activeElementRef,
    onTimestampClick: handleTimestampClick,
    onWordClick: handleTimestampClick
  };
}
